import React, { useContext, useState } from "react";
import type { EmailSchedule } from "@components/job_alerts/types";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@/sprout/flash";
import { type Option, SingleSelect } from "@sprout/select";
import { router } from "@inertiajs/react";

interface EmailSchedules {
  emailSchedule: EmailSchedule;
  emailSchedules: { [key: string]: number };
  notificationSchedulePath: string;
}

export const EmailSchedulesContext = React.createContext<EmailSchedules>(
  null as unknown as EmailSchedules,
);

interface Props {
  shortCadence?: boolean;
}

const Preferences = ({ shortCadence = false }: Props) => {
  const { t } = useTranslation("jobAlerts");
  const showFlash = useShowFlash();
  const { emailSchedule, emailSchedules, notificationSchedulePath } =
    useContext(EmailSchedulesContext);

  const getCadenceText = (cadence: string) => {
    return shortCadence
      ? cadence.charAt(0).toUpperCase() + cadence.slice(1)
      : t("preferences.sendEmailCadence", { cadence });
  };

  const scheduleOptions = Object.keys(emailSchedules).map((cadence) => ({
    value: emailSchedules[cadence],
    label: getCadenceText(cadence),
  }));

  const defaultEmailSchedule =
    scheduleOptions.find((option) => option.value === emailSchedule.id) || scheduleOptions[0];

  const [schedule, setSchedule] = useState<Option>(defaultEmailSchedule);

  const updateEmailSchedule = async (updatedSchedule: Option) => {
    const cadence =
      updatedSchedule.value === emailSchedules["daily"]
        ? t("preferences.daily")
        : t("preferences.weekly");
    router.patch(
      notificationSchedulePath,
      { cadence },
      {
        onFinish: () => {
          showFlash(t("preferences.frequencyUpdated"));
          setSchedule(updatedSchedule);
        },
        onError: () => {
          showFlash(t("preferences.updateFailed"), "error");
        },
      },
    );
  };

  return (
    <div className="preferences">
      <SingleSelect
        onChange={(value) => updateEmailSchedule(value as Option)}
        value={schedule}
        label=""
        options={scheduleOptions}
      />
    </div>
  );
};

export default Preferences;
